import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, lastValueFrom, map, Observable, shareReplay, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { BehaviorSubject, of } from 'rxjs';
import { InvestmentChecklistVariableModel } from 'src/app/models/investment-checklist-variables.model';
import { MarketUtilsService } from './market-utils.service';
import { WebsiteStats } from 'src/app/models/website-stats/website.stats.modal';
// import { MarketUtilsService } from './market-utils.service';
import { TYPESENSE_COLLECTIONS } from '../constants/typesense-collections';
import { CrowdfundingProgressbarResponse, CrowdfundingProgressbarResponse3, CrowdfundingProgressbarResponse5 } from 'src/app/models/crowdfunding/progressbar.model';
import { getCountryNameByCode } from 'src/app/utils/countries';
import { Methodology, RawMethodologyResponse } from 'src/app/features/stock-details/types/screening-methodology.type';
import { CountryCoverageHit, CountryCoverageRecord, CountryCoverageResponse } from 'src/app/models/country-coverage.model';
import { TradingCountryInfo, TradingCountryResponse } from 'src/app/models/trading-countries.model';

interface PercentOffEndDate { 
   '50_percent_off_end_date': string, 
   id: string 
}

export interface BannersVisibility {
   id: string;
   carousalBanner: boolean;
   popupBanner: boolean;
   popupBannerRiba: boolean;
   stickyBanner: boolean;
}

export interface FoundationOffer {
   id: string,
   foundation_monthly_offer_chip: string,
   foundation_yearly_offer_chip: string,
   foundation_monthly_offer_subtext: string,
   foundation_yearly_offer_subtext: string
}

@Injectable({
   providedIn: 'root',
})
export class TypesenseService {
   private dividendYieldData = new BehaviorSubject<any>(null);
   public dividendYieldData$ = this.dividendYieldData.asObservable();

   private websiteStats$!: Observable<WebsiteStats>;
   private portfolioStats$!: Observable<any>;
   private stocksDataCache = new Map<string, { data: any, expiresAt: number }>();

   // Cached observable
   private endDate$: Observable<PercentOffEndDate> | null = null;

   private bannersVisibilityData$: Observable<BannersVisibility> | null = null;
   private foundationOfferData$: Observable<FoundationOffer> | null = null;
   private countryCoverageData$!: Observable<CountryCoverageRecord[]>;
   private tradingCountriesData$!: Observable<TradingCountryInfo[]>;
   private progressbar5$?: Observable<CrowdfundingProgressbarResponse5>;

   private configData$?: Observable<any>;

   constructor(
      private auth: AuthService,
      private http: HttpClient,
      private marketUtilsService: MarketUtilsService
   ) { }

   searchStocksEtfsAndIpos(
      name: string,
      stockCountries: any = [],
      etfCountries: any = [],
      userCountry: string | null
   ): any {
      if (name === '') {
         return of([]);
      }

      const searches = [
         {
            collection: TYPESENSE_COLLECTIONS.COMPANY_PROFILE,
            q: name,
            query_by: 'name,ticker',
            prioritize_token_position: true,
            sort_by: '_text_match:desc,tickerIsMain:desc,usdMarketCap:desc',
            query_by_weights: '1,2',
            filter_by: `status:=PUBLISH&&country:=[${stockCountries}]`,
         },
         {
            collection: TYPESENSE_COLLECTIONS.ETF_PROFILE,
            q: name,
            query_by: 'name,symbol',
            prioritize_token_position: true,
            sort_by: '_text_match:desc,aum:desc',
            query_by_weights: '1,2',
            filter_by: `domicile:=[${etfCountries}]`,
         },
         {
            collection: TYPESENSE_COLLECTIONS.IPO_PROFILE_COLLECTION,
            q: name,
            query_by: 'name,ticker',
            prioritize_token_position: true,
            sort_by: '_text_match:desc,marketCapitalization:desc',
            query_by_weights: '1,2',
            filter_by: `sharia_compliance:!=NOT_UNDER_COVERAGE`,
         },
      ];
      return this.http
         .post<any>(environment.typeSense.url + '/multi_search', { searches })
         .pipe(
            map((item) => {
               const results: any = item?.results;
               const ret: any[] = [];

               if (results?.[0]?.hits?.length > 0) {
                  this.processStockData(results[0].hits, ret, name);
               }
               if (results?.[1]?.hits?.length > 0) {
                  this.processEtfData(results[1].hits, ret, name);
               }
               if (results?.[2]?.hits?.length > 0) {
                  this.processIpoData(results[2].hits, ret, name);
               }
               return ret.sort((a, b) => b.textMatch - a.textMatch);
            })
         );
   }

   searchStocksAndEtfs(
      name: string,
      stockCountries: any = [],
      etfCountries: any = [],
      userCountry: string | null
   ): any {
      if (name === '') {
         return of([]);
      }

      const searches = [
         {
            collection: TYPESENSE_COLLECTIONS.COMPANY_PROFILE,
            q: name,
            query_by: 'name,ticker',
            prioritize_token_position: true,
            sort_by: `_text_match:desc,$${TYPESENSE_COLLECTIONS.STOCKS_DATA}(isMainTicker:desc,usdMarketCap:desc)`,
            query_by_weights: '1,2',
            filter_by: `$${TYPESENSE_COLLECTIONS.STOCKS_DATA}(status:=PUBLISH&&country:=[${stockCountries}])`,
            include_fields: `*,$${TYPESENSE_COLLECTIONS.STOCKS_DATA}(id,sharia_compliance,ranking,ranking_v2)`
         },
         {
            collection: TYPESENSE_COLLECTIONS.ETF_PROFILE,
            q: name,
            query_by: 'name,symbol',
            prioritize_token_position: true,
            sort_by: `_text_match:desc,$${TYPESENSE_COLLECTIONS.ETFS_DATA}(aum:desc)`,
            query_by_weights: '1,2',
            filter_by: `$${TYPESENSE_COLLECTIONS.ETFS_DATA}(domicile:=[${etfCountries}])`,
            include_fields: `*,$${TYPESENSE_COLLECTIONS.ETFS_DATA}(id,aum,domicile,shariahCompliantStatus,ranking_v2)`
         },
      ];
      return this.http
         .post<any>(environment.typeSense.url + '/multi_search', { searches })
         .pipe(
            map((item) => {
               const results: any = item?.results;
               const ret: any[] = [];

               if (results?.[0]?.hits.length > 0) {
                  this.processStockData(results[0].hits, ret, name);
               }
               if (results?.[1]?.hits.length > 0) {
                  this.processEtfData(results[1].hits, ret, name);
               }
               // results.forEach((item: any) => {
               //    const data: any = item.hits;
               //    if (data) {
               //       if (
               //          item.request_params?.collection_name.includes(
               //             TYPESENSE_COLLECTIONS.COMPANY_PROFILE
               //          )
               //       ) {
               //          this.processStockData(data, ret, name);
               //       } else {
               //          this.processEtfData(data, ret, name);
               //       }
               //    }
               // });
               return ret.sort((a, b) => b.textMatch - a.textMatch);
            })
         );
   }

   private processStockData(data: any, ret: any[], name: string) {
      data.forEach((x: any) => {
         const doc = x.document;
         const tickerSegments = doc.ticker.split('.');
         let textMatch = x.text_match;
         if (tickerSegments.includes(name.toUpperCase())) {
            textMatch += textMatch;
         }
         ret.push({
            canAddToWatchlist: true,
            companyName: doc.name,
            countryName: doc.country,
            exchange: doc.exchange,
            flag: doc.country.toLowerCase(),
            logo: doc.logo,
            identifier: doc.identifier,
            stockName: doc.ticker,
            type: 'STOCK',
            textMatch: textMatch,
            isin: doc.isin,
            mainTicker: doc.mainTicker,
            currency: doc.currency,
            complianceStatus: doc.shariahCompliantStatus,
            ranking: doc.compliantRanking,
            change: doc.change,
            price: doc.currentPrice,
         });
      });
   }

   private processIpoData(data: any, ret: any[], name: string) {
      data.forEach((x: any) => {
         const doc = x.document;
         const tickerSegments = doc?.ticker?.split('.');
         let textMatch = x.text_match;
         if (tickerSegments?.includes(name.toUpperCase())) {
            textMatch += textMatch;
         }
         if(!ret.some((item: any) => item.stockName === doc.ticker)) {
            ret.push({
               canAddToWatchlist: false,
               companyName: doc.name,
               countryName: doc.country,
               exchange: doc.exchange_symbol,
               flag: doc.country.toLowerCase(),
               logo: doc.logo,
               identifier: doc.id,
               stockName: doc.ticker,
               type: 'IPO',
               textMatch: textMatch,
               currency: doc.currency
            });
         }
      });
   }

   private processEtfData(data: any, ret: any[], name: string) {
      data.forEach((x: any) => {
         const doc = x.document;
         const tickerSegments = doc.symbol.split('.');
         let textMatch = x.text_match;
         if (tickerSegments.includes(name)) {
            textMatch += textMatch;
         }
         ret.push({
            canAddToWatchlist: true,
            companyName: doc.name,
            countryName: doc.domicile,
            exchange: doc.exchange,
            flag: doc.domicile.toLowerCase(),
            identifier: doc.identifier,
            stockName: doc.symbol,
            type: 'ETF',
            textMatch: textMatch,
            currency: doc.navCurrency,
            complianceStatus: doc.shariahStates,
            ranking: doc.ranking,
            change: doc.change,
            price: doc.currentPrice,
         });
      });
   }

   public searchStockTypeSense(
      name: string,
      stockCountries: any = [],
      etfCountries: any = [],
      userCountry: string | null
   ): any {
      return this.http
         .post<any>(environment.typeSense.url + '/multi_search', {
            searches: [
               {
                  collection: TYPESENSE_COLLECTIONS.COMPANY_PROFILE,
                  q: name,
                  query_by: 'name,ticker',
                  prioritize_token_position: true,
                  // "prioritize_exact_match": true,
                  sort_by:
                     '_text_match:desc,tickerIsMain:desc,usdMarketCap:desc',
                  query_by_weights: '1,2',
                  // text_match_type: 'max_weight',
                  filter_by: `status:=PUBLISH&&country:=[${stockCountries}]`,
               },
               // {
               //    collection: TYPESENSE_COLLECTIONS.COMPANY_PROFILE,
               //    q: name,
               //    query_by: 'name,ticker',
               //    prioritize_token_position: true,
               //    // "prioritize_exact_match": true,
               //    sort_by:
               //       '_text_match:desc,tickerIsMain:desc,usdMarketCap:desc',
               //    query_by_weights: '1,2',
               //    // text_match_type: 'max_weight',
               //    num_typos: '0',
               //    filter_by: `status:=PUBLISH${
               //       userCountry ? `&&country:=[${userCountry}]` : ''
               //    }`,
               // },
               {
                  collection: TYPESENSE_COLLECTIONS.ETF_PROFILE,
                  q: name,
                  query_by: 'name,symbol',
                  prioritize_token_position: true,
                  // "prioritize_exact_match": true,
                  sort_by: '_text_match:desc,aum:desc',
                  query_by_weights: '1,2',
                  // text_match_type: 'max_weight',
                  filter_by: `domicile:=[${etfCountries}]`,
               },
            ],
         })
         .pipe(
            map((item) => {
               const results: any = item?.results;

               const ret: any[] = [];

               results.forEach((item: any) => {
                  const data: any = item.hits;
                  if (data) {
                     if (
                        item.request_params?.collection_name.includes(
                           'company_profile'
                        )
                     ) {
                        data.forEach((x: any) => {
                           const doc = x.document;
                           const tickerSegments = doc.ticker.split('.');
                           let textMatch = x.text_match;
                           if (tickerSegments.includes(name.toUpperCase())) {
                              textMatch += textMatch;
                           }
                           ret.push({
                              canAddToWatchlist: true,
                              companyName: doc.name,
                              countryName: doc.country,
                              exchange: doc.exchange,
                              flag: doc.country.toLowerCase(),
                              identifier: doc.identifier,
                              stockName: doc.ticker,
                              type: 'STOCK',
                              textMatch: textMatch,
                              isin: doc.isin,
                              mainTicker: doc.mainTicker,
                              currency: doc.currency,
                              complianceStatus: doc.shariahCompliantStatus,
                              ranking: doc.compliantRanking,
                           });
                        });
                     } else {
                        data.forEach((x: any) => {
                           const doc = x.document;
                           const tickerSegments = doc.symbol.split('.');
                           let textMatch = x.text_match;
                           if (tickerSegments.includes(name)) {
                              textMatch += textMatch;
                           }
                           ret.push({
                              canAddToWatchlist: true,
                              companyName: doc.name,
                              countryName: doc.domicile,
                              exchange: doc.exchange,
                              flag: doc.domicile.toLowerCase(),
                              identifier: doc.identifier,
                              stockName: doc.symbol,
                              type: 'ETF',
                              textMatch: textMatch,
                              currency: doc.navCurrency,
                              complianceStatus: doc.shariahStates,
                              ranking: doc.ranking,
                           });
                        });
                     }
                  }
               });
               return ret.sort((a, b) => b.textMatch - a.textMatch);
            })
         );
   }

   public searchOnlyStockTypeSense(name: string, countries: any[], limit: number = 10): any {
      return this.http
         .post<any>(environment.typeSense.url + '/multi_search', {
            searches: [
               {
                  collection: TYPESENSE_COLLECTIONS.COMPANY_PROFILE,
                  q: name,
                  per_page: limit,
                  query_by: 'name,ticker',
                  prioritize_token_position: true,
                  sort_by:
                     '_text_match:desc,tickerIsMain:desc,usdMarketCap:desc',
                  query_by_weights: '1,1',
                  filter_by:
                     `country:[${countries.join(',')}]&&status:=PUBLISH`,
               },
            ],
         })
         .pipe(
            map((item) => {
               const results: any = item?.results;

               const ret: any[] = [];

               results.forEach((item: any) => {
                  const data: any = item.hits;
                  if (
                     item.request_params?.collection_name.includes(
                        'company_profile'
                     )
                  ) {
                     data.forEach((x: any) => {
                        const doc = x.document;
                        ret.push({
                           canAddToWatchlist: true,
                           companyName: doc.name,
                           countryName: doc.country,
                           exchange: doc.exchange,
                           flag: doc.country.toLowerCase(),
                           identifier: doc.identifier,
                           stockName: doc.ticker,
                           type: 'STOCK',
                           textMatch: x.text_match,
                           status: doc.shariahCompliantStatus,
                           currency: doc.currency,
                        });
                     });
                  } else {
                     data.forEach((x: any) => {
                        const doc = x.document;
                        ret.push({
                           canAddToWatchlist: true,
                           companyName: doc.name,
                           countryName: doc.domicile,
                           exchange: doc.exchange,
                           flag: doc.domicile.toLowerCase(),
                           identifier: doc.identifier,
                           stockName: doc.symbol,
                           type: 'ETF',
                           textMatch: x.text_match,
                           status: doc.shariahCompliantStatus,
                           currency: doc.currency,
                        });
                     });
                  }
               });
               return ret.sort((a, b) => b.textMatch - a.textMatch);
            })
         );
   }

   public getPopularStocksTypeSense(
      page: number,
      sector: string,
      industryList: string[],
      compliance: string[],
      country: string,
      per_page: number = 15
   ): Observable<any> {
      const filterBy = [];
      const symbols: any[] = [];
      const sortBy = ['usdMarketCap:desc', 'exchangeMarketCap:desc'];
      filterBy.push('status:=PUBLISH');
      filterBy.push('exchange:!=OTC');
      filterBy.push('tickerIsMain:=true');
      if (country) {
         filterBy.push('country:=' + country);
         // if(country === 'US') {
         //    sortBy.push('usdMarketCap:desc')
         // }
      }
      if (sector) {
         filterBy.push('musaffaSectorName:=' + sector);
      }
      if (industryList.length > 0) {
         filterBy.push('musaffaIndustryName:[' + industryList.join(',') + ']');
      }

      // sortBy.push('usdMarketCap:desc')

      // const groupBy = 'group_by=mainTicker&group_limit=1&'
      // return this.http.get<any>(`${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/search?${groupBy}sort_by=${sortBy.join(',')}&page=${page}&per_page=50&q=*&filter_by=${filterBy.join('&&')}`)
      //    .pipe(map((item) => {
      //    const items: any[] = []
      //    const data: any = item.grouped_hits
      //    data.forEach((x: any) => {
      //       const doc = x.hits[0].document
      //       symbols.push(doc.isin)
      //       items.push({
      //          companyName: doc.name,
      //          currency: doc.currency,
      //          exchange: doc.exchange,
      //          exchangeMarketCap: doc.exchangeMarketCap,
      //          identifier: doc.identifier,
      //          industry: doc.musaffaIndustryName,
      //          isin: doc.isin,
      //          logo: doc.logo,
      //          marketCapitalization: doc.marketCapitalization,
      //          stockName: doc.ticker,
      //          sector: doc.musaffaSectorName,
      //       })
      //    })
      //    const number_of_pages = Math.ceil(item.found/item.request_params.per_page)
      //    return {
      //       currentPage: item.page,
      //       hasNext: item.page < number_of_pages,
      //       hasPrevious: item.page > 1,
      //       itemCount: item.request_params.per_page,
      //       pageQuantity: number_of_pages,
      //       totalCount: item.found,
      //       items: items,
      //       symbols: symbols
      //    }
      // }))
      return this.http
         .get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE
            }/documents/search?sort_by=${sortBy.join(
               ','
            )}&page=${page}&per_page=${per_page}&q=*&filter_by=${filterBy.join(
               '&&'
            )}&&shariahCompliantStatus:=${compliance}`
         )
         .pipe(
            map((item) => {
               const items: any[] = [];
               const data: any = item.hits;
               data.forEach((x: any) => {
                  const doc = x.document;
                  symbols.push(doc.isin);
                  items.push({
                     companyName: doc.name,
                     currency: doc.currency,
                     exchange: doc.exchange,
                     exchangeMarketCap: doc.exchangeMarketCap,
                     identifier: doc.identifier,
                     industry: doc.musaffaIndustryName,
                     isin: doc.isin,
                     mainTicker: doc.mainTicker,
                     logo: doc.logo,
                     marketCapitalization: doc.marketCapitalization,
                     stockName: doc.ticker,
                     sector: doc.musaffaSectorName,
                  });
               });
               const number_of_pages = Math.ceil(
                  item.found / item.request_params.per_page
               );
               return {
                  currentPage: item.page,
                  hasNext: item.page < number_of_pages,
                  hasPrevious: item.page > 1,
                  itemCount: item.request_params.per_page,
                  pageQuantity: number_of_pages,
                  totalCount: item.found,
                  items: items,
                  symbols: symbols,
                  shariahCompliantStatus: item.shariahCompliantStatus,
               };
            })
         );
   }
   private exchangeDataCache: Record<string, any[]> = {};

   public async getStocksExchangeData(countryCode: string, type: string): Promise<any[]> {
      const cacheKey = `${countryCode}_${type}`;
      if (this.exchangeDataCache[cacheKey]) {
         return this.exchangeDataCache[cacheKey];
      }

      const countriesData = await this.marketUtilsService.fetchCountries();
      if (countriesData) {
         const currentCountryObj = countriesData.find(
            (obj: any) => obj.countryCode === countryCode
         ) ?? { stockExchanges: [], etfExchanges: [] };

         const exchangeArr = type === 'stock'
            ? currentCountryObj.stockExchanges
            : currentCountryObj.etfExchanges;

         this.exchangeDataCache[cacheKey] = exchangeArr;
         return exchangeArr;
      }
      return [];
   }

   public async getStocksFromSymbols(symbols: string[]): Promise<any> {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('filter_by', `id:=[${symbols.join(',')}]`);

      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/search`,
               { params: queryParams }
            )
            .pipe(
               map((item) => {
                  const items: any[] = [];
                  const data: any = item.hits;
                  data.forEach((x: any) => {
                     const doc = x.document;
                     symbols.push(doc.mainTicker);
                     items.push({
                        companyName: doc.name,
                        currency: doc.currency,
                        exchange: doc.exchange,
                        exchangeMarketCap: doc.exchangeMarketCap,
                        identifier: doc.identifier,
                        industry: doc.musaffaIndustryName,
                        isin: doc.isin,
                        mainTicker: doc.mainTicker,
                        logo: doc.logo,
                        marketCapitalization: doc.marketCapitalization,
                        stockName: doc.ticker,
                        sector: doc.musaffaSectorName,
                        percentChange: doc.percentChange,
                        currentPrice: doc.currentPrice,
                        recommededStatus: doc.analyst_recommendation_weighted_avg,
                        complianceStatus: doc.shariahCompliantStatus,
                        complianceRanking: doc.compliantRanking,
                     });
                  });
                  const number_of_pages = Math.ceil(
                     item.found / item.request_params.per_page
                  );
                  return {
                     currentPage: item.page,
                     hasNext: item.page < number_of_pages,
                     hasPrevious: item.page > 1,
                     itemCount: item.request_params.per_page,
                     pageQuantity: number_of_pages,
                     totalCount: item.found,
                     items: items,
                     symbols: symbols,
                  };
               })
            )
      );
   }

   public async getPopularStocksTypeSenseV2(
      page: number,
      sector: string,
      exchange: string[],
      industryList: string[],
      compliance: string[],
      complianceScore: string[],
      country: string,
      per_page: number = 15,
      recommededStatus: string[],
      sortCol: any,
      sortOrder: string,
      sort?: string,
      showPrimary: boolean = true,
      sectors: string[] = [],
      marketCapClassification?: string
   ): Promise<any> {
      const filterBy = [];
      const symbols: any[] = [];
      const sortBy = ['usdMarketCap:desc', 'exchangeMarketCap:desc'];

      filterBy.push('status:=PUBLISH');
      if (showPrimary) {
         filterBy.push('tickerIsMain:=true');
      }
      if (country) {
         filterBy.push('country:=' + country?.toUpperCase());
      }
      if (exchange && exchange.length) {
         filterBy.push('exchange:=[' + exchange.join(',') + ']');
      } else {
         filterBy.push('exchange:!=OTC');
         if (country?.toUpperCase() === 'US') {
            filterBy.push('exchange:=[NYSE,NASDAQ]');
         }
         // const list: any = await this.getStocksExchangeData(country, 'stock')
         // filterBy.push('exchange:=[' + list.join(',') + ']');
      }
      // console.log("sector "+sector)
      if (sector?.length) {
         filterBy.push(`musaffaSectorName:=\`${sector?.trim()}\``);
      } else if (sectors?.length) {
         filterBy.push(`musaffaSectorName:=[${sectors?.join(',')}]`);
      }

      if (sort) {
         sortBy.unshift(sort);
      }
      // if (industryList && industryList.length > 0) {
      //    let formattedArr = industryList.map((item) => '`' + item + '`');
      //    let str = `musaffaIndustryName:=[${formattedArr.join(',')}]`;
      //    filterBy.push(str);
      // }
      if (marketCapClassification) {
         filterBy.push('marketCapClassification:=' + marketCapClassification);
      }

      if (compliance && compliance.length) {
         if (compliance.includes('')) {
            filterBy.push(
               "shariahCompliantStatus:=['COMPLIANT', 'NON_COMPLIANT', 'QUESTIONABLE']"
            );
         } else if (!compliance.includes('')) {
            let formattedArr = compliance.map((item: any) =>
               item ? '`' + item + '`' : ''
            );
            let str = `shariahCompliantStatus:=[${formattedArr.join(',')}]`;
            filterBy.push(str);
         }
      } else {
         filterBy.push(
            "shariahCompliantStatus:=['COMPLIANT', 'NON_COMPLIANT', 'QUESTIONABLE']"
         );
      }

      // if (compliance && compliance.length) {
      //    let formattedArr;

      //    if(compliance.includes('')) {
      //       formattedArr = compliance.map((item : any) => item ? '`' + item + '`' : '')
      //       let str = `shariahCompliantStatus:=[${formattedArr.join(',')}]`;
      //       filterBy.push(str);
      //    } else if(!compliance.includes('')) {
      //       formattedArr = compliance.map((item : any) => item ? '`' + item + '`' : '')
      //       let str = `shariahCompliantStatus:=[${formattedArr.join(',')}]`;
      //       filterBy.push(str);
      //    } else {
      //       formattedArr = [];
      //    }

      // }

      if (complianceScore && complianceScore.length) {
         filterBy.push('compliantRanking:=[' + complianceScore.join(',') + ']');
      }

      if (recommededStatus && recommededStatus.length) {
         filterBy.push(
            'analyst_recommendation_weighted_avg:=[' +
            recommededStatus.join(',') +
            ']'
         );
      }

      if (sortCol && sortOrder) {
         sortBy.unshift(`${sortCol}:${sortOrder}`);
      }

      let queryParams = new HttpParams()
         .set('sort_by', sortBy.join(','))
         .set('page', page)
         .set('per_page', per_page)
         .set('q', '*')
         .set('filter_by', filterBy.join('&&'));

      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/search`,
               { params: queryParams }
            )
            .pipe(
               map((item) => {
                  const items: any[] = [];
                  const data: any = item.hits;
                  data.forEach((x: any) => {
                     const doc = x.document;
                     symbols.push(doc.mainTicker);
                     items.push({
                        companyName: doc.name,
                        currency: doc.currency,
                        exchange: doc.exchange,
                        exchangeMarketCap: doc.exchangeMarketCap,
                        identifier: doc.identifier,
                        industry: doc.musaffaIndustryName,
                        isin: doc.isin,
                        mainTicker: doc.mainTicker,
                        logo: doc.logo,
                        marketCapitalization: doc.marketCapitalization,
                        stockName: doc.ticker,
                        sector: doc.musaffaSectorName,
                        percentChange: doc.percentChange,
                        currentPrice: doc.currentPrice,
                        recommededStatus: doc.analyst_recommendation_weighted_avg,
                        complianceStatus: doc.shariahCompliantStatus,
                        complianceRanking: doc.compliantRanking,
                     });
                  });
                  const number_of_pages = Math.ceil(
                     item.found / item.request_params.per_page
                  );
                  return {
                     currentPage: item.page,
                     hasNext: item.page < number_of_pages,
                     hasPrevious: item.page > 1,
                     itemCount: item.request_params.per_page,
                     pageQuantity: number_of_pages,
                     totalCount: item.found,
                     items: items,
                     symbols: symbols,
                  };
               })
            )
      );
   }
   // public async getStockComplianceFromSymbols(symbols: any) {
   //    const filterBy =
   //       symbols.length > 0 ? `filter_by=id:[${symbols.join(',')}]` : '';
   //    return await lastValueFrom(
   //       this.http
   //          .get<any>(
   //             `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPLIANCE}/documents/search?per_page=250&x-typesense-api-key=${environment.typeSense.searchApiKey}&q=*&${filterBy}`
   //          )
   //          .pipe(
   //             map((item) => {
   //                const data: any = item.hits;
   //                let ret: any = {};
   //                data.forEach((x: any) => {
   //                   const doc = x.document;
   //                   ret[doc.id] = {
   //                      status: doc.status,
   //                      ranking: doc.ranking,
   //                   };
   //                });

   //                return ret;
   //             })
   //          )
   //    );
   // }

   async getEtfsCollection<T>(
      country: string,
      page: number,
      per_page: number,
      sort_by: string,
      filter_by: any
   ): Promise<T> {
      let params = new HttpParams()
         .set('q', '*')
         .set('country', country)
         .set('page', page)
         .set('per_page', per_page)
         .set('sort_by', sort_by)
         .set('filter_by', filter_by);

      return await lastValueFrom(
         this.http.get<T>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_PROFILE}/documents/search`,
            { params }
         )
      );
   }

   public async getPopularEtfTypeSense(
      country: string,
      page: number,
      filterData: any,
      per_page: number = 15,
      sortCol: any,
      sortOrder: string
   ): Promise<any> {
      // console.log(sortCol);
      // console.log(sortOrder);
      const filterBy = [];
      const sortBy = ['aum:desc'];

      if (filterData != null) {
         if (country) {
            filterBy.push('domicile:=[' + country + ']');
         } else {
            filterBy.push('domicile:=[US]');
         }
         if (filterData.exchange && filterData.exchange.length) {
            filterBy.push('exchange:=[' + filterData.exchange.join(',') + ']');
         } else {
            const list: any = await this.getStocksExchangeData(country, 'etf');
            if (list.length) {
               filterBy.push('exchange:=[' + list.join(',') + ']');
            }
         }
         if (filterData.assetClass && filterData.assetClass.length) {
            filterBy.push(
               'assetClass:=[' + filterData.assetClass.join(',') + ']'
            );
         } else {
            // let classData = filterData.etfClass.map((item: any) => item.key);
            // filterBy.push('assetClass:=[' + classData.join(',') + ']');
         }
         if (filterData.shariahStates && filterData.shariahStates.length) {
            // let formattedArr = compliance.map((item)=>item ? "`"+item+"`" : '')
            // let str = `shariahCompliantStatus:=[${formattedArr.join(',')}]`
            // filterBy.push(str)

            let formattedArr;

            if (filterData.shariahStates.includes('')) {
               // formattedArr = filterData.shariahStates.map((item: any) =>
               //    item ? '`' + item + '`' : ''
               // );
               // let str = `shariahStates:=[${formattedArr.join(',')}]`;
               // filterBy.push(str);
               formattedArr = ['COMPLIANT', 'NON_COMPLIANT', 'QUESTIONABLE'];
               let str = `shariahStates:=[${formattedArr.join(',')}]`;
               filterBy.push(str);
            } else if (!filterData.shariahStates.includes('')) {
               formattedArr = filterData.shariahStates.map((item: any) =>
                  item ? '`' + item + '`' : ''
               );
               let str = `shariahStates:=[${formattedArr.join(',')}]`;
               filterBy.push(str);
            }
         } else {
            let formattedArr = ['COMPLIANT', 'NON_COMPLIANT', 'QUESTIONABLE'];
            let str = `shariahStates:=[${formattedArr.join(',')}]`;
            filterBy.push(str);
         }

         if (filterData.ranking && filterData.ranking.length) {
            filterBy.push('ranking:=[' + filterData.ranking.join(',') + ']');
         }

         if (sortCol && sortOrder) {
            sortBy.unshift(`${sortCol}:${sortOrder}`);
         }

         if (filterData.investmentSegment) {
            filterBy.push('investmentSegment:=' + filterData.investmentSegment);
         }
      }
      //
      let queryParams = new HttpParams()
         .set('sort_by', sortBy.join(','))
         .set('page', page)
         .set('per_page', per_page)
         .set('q', '*')
         .set('filter_by', filterBy.join('&&'));
      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_PROFILE}/documents/search`,
               { params: queryParams }
            )
            .pipe(
               map((item) => {
                  const items: any[] = [];
                  const data: any = item.hits;
                  data.forEach((x: any) => {
                     const doc = x.document;

                     items.push({
                        assetClass: doc.assetClass,
                        aum: doc.aum,
                        avoid: doc.avoid,
                        cusip: doc.cusip,
                        description: doc.description,
                        domicile: doc.domicile,
                        etfCompany: doc.etfCompany,
                        exchange: doc.exchange,
                        expenseRatio: doc.expenseRatio,
                        halal: doc.halal,
                        identifier: doc.identifier,
                        inceptionDate: doc.inceptionDate,
                        investmentSegment: doc.investmentSegment,
                        isin: doc.isin,
                        mainSymbol: doc.mainSymbol,
                        name: doc.name,
                        nav: doc.nav,
                        navCurrency: doc.navCurrency,
                        notHalal: doc.notHalal,
                        numberOfHoldings: doc.numberOfHoldings,
                        shariahStates: doc.shariahStates,
                        symbol: doc.symbol,
                        trackingIndex: doc.trackingIndex,
                        unCovered: doc.unCovered,
                        ranking: doc.ranking,

                        status: null, //
                        price: null, //
                        priceChange: null, //
                        priceChangePercent: null, //
                        lastPrice: null, //
                     });
                  });
                  const number_of_pages = Math.ceil(
                     item.found / item.request_params.per_page
                  );
                  return {
                     currentPage: item.page,
                     hasNext: item.page < number_of_pages,
                     hasPrevious: item.page > 1,
                     itemCount: item.request_params.per_page,
                     pageQuantity: number_of_pages,
                     totalCount: item.found,
                     items: items,
                  };
               })
            )
      );
   }

   public async getEtfFromSymbols(symbols: any) {
      symbols = symbols.filter((symbol: any) => !!symbol?.length);

      let filterBy = `symbol:=[${symbols.join(',')}]`;
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('per_page', '250')
         .set('filter_by', filterBy);

      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_PROFILE}/documents/search`,
               { params: queryParams }
            )
            .pipe(
               map((item) => {
                  const items: any = {};
                  const data: any = item.hits;
                  data.forEach((x: any) => {
                     const doc = x.document;
                     items[doc.symbol] = doc;
                  });
                  return items;
               })
            )
      );
   }

   public async getStockFromSymbols(symbols: any) {
      symbols = symbols.filter((symbol: any) => !!symbol?.length);
      const chunkSize = 30;
      const data = [];
      const items: any = {};
      for (let i = 0; i < symbols.length; i += chunkSize) {
         let chunk = symbols.slice(i, i + chunkSize);

         let filterBy = `ticker:[${chunk.join(',')}]`;

         let queryParams = new HttpParams()
            .set('q', '*')
            .set('per_page', '250')
            .set('filter_by', filterBy);

         // return lastValueFrom(
         //    this.http.get<any>(
         //       `${environment.typeSense.url}/collections/bse_tickers/documents/search`,
         //       { params: queryParams }
         //    )
         // );

         await lastValueFrom(
            this.http
               .get<any>(
                  `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/search`,
                  { params: queryParams }
               )
               .pipe(
                  map((item) => {
                     const data: any = item.hits;
                     data.forEach((x: any) => {
                        const doc = x.document;
                        if (symbols.includes(doc.ticker)) {
                           items[doc.ticker] = doc;
                        }
                     });
                     return items;
                  })
               )
         );
      }

      return items;
   }

   public getRecentlyViewed() {
      const userId = this.auth.getUserId() ?? null;
      return this.http
         .get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.USER_RECENTLY_VIEWED}/documents/search?q=*&sort_by=last_viewed_at:desc&filter_by=user_id:=${userId}&per_page=50`
         )
         .pipe(
            map((item) => {
               const items: any = {};
               const stockSymbols: any[] = [];
               const etfSymbols: any[] = [];
               const data: any = item.hits;
               data.forEach((x: any) => {
                  const doc = x.document;
                  if (doc.type === 'stock') {
                     stockSymbols.push(doc.symbol);
                     items[doc.symbol] = {
                        companyName: null,
                        // compliantRanking: 0,
                        currency: null,
                        identifier: null,
                        isin: doc.isin,
                        // shariahCompliantStatus: "QUESTIONABLE",
                        stockName: doc.symbol,
                        type: 'STOCK',
                     };
                  } else if (doc.type === 'etf') {
                     etfSymbols.push(doc.symbol);
                     items[doc.symbol] = {
                        domicile: null,
                        etfCompany: null,
                        identifier: null,
                        name: null,
                        nav: null,
                        navCurrency: null,
                        isin: doc.isin,
                        symbol: doc.symbol,
                        type: 'ETF',
                     };
                  }
               });

               return {
                  viewedItems: items,
                  stockSymbols: stockSymbols,
                  etfSymbols: etfSymbols,
               };
            })
         );
   }

   public getCompanyProfileObservable(ticker: string) {
      return this.http.get<any>(
         `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/${ticker}`
      );
   }

   private companyProfileCache = new Map<string, {data: any, expiresAt: number}>();

   public getCompanyProfile(ticker: string) {
      // Check cache first
      const cached = this.companyProfileCache.get(ticker);
      if (cached && cached.expiresAt > Date.now()) {
         return of(cached.data);
      }

      return this.http
         .get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/${ticker}`
         )
         .pipe(
            map((item) => {
               if (item) {
                  const {
                     debt,
                     logs,
                     notHalal,
                     questionable,
                     ranking,
                     reportSource,
                     securitiesAndAssets,
                     source,
                     status,
                     totalRevenue,
                     trailing36MonAvrCap,
                     ...companyProfileData
                  } = item;

                  // Cache the mapped data for 6 hours
                  this.companyProfileCache.set(ticker, {
                     data: companyProfileData,
                     expiresAt: Date.now() + (6 * 60 * 60 * 1000) // 6 hours in milliseconds
                  });

                  return companyProfileData;
               } else {
                  return item;
               }
            })
         );
   }

   // public async getStockComplianceInfo(mainTicker: string): Promise<any> {
   //    return await lastValueFrom(
   //       this.http.get<any>(
   //          `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPLIANCE}/documents/${mainTicker}?x-typesense-api-key=${environment.typeSense.searchApiKey}`
   //       )
   //    );
   // }

   public getCompanyFundamentals(ticker: string): any {
      // Check if data exists in cache and not expired
      const cached = this.stocksDataCache.get(ticker);
      if (cached && cached.expiresAt > Date.now()) {
         return of(cached.data);
      }

      // If not in cache or expired, make HTTP request and cache result
      return this.http
         .get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA
            }/documents/${encodeURIComponent(ticker)}`
         )
         .pipe(
            map((doc) => {
               const mappedData = this.mapPricesData(doc);
               // Cache with 2 minute expiration
               this.stocksDataCache.set(ticker, {
                  data: mappedData,
                  expiresAt: Date.now() + 2 * 60 * 1000 // 2 minutes in milliseconds
               });
               return mappedData;
            })
         );
   }

   // public getCompanyFundamentals(ticker: string): any {
   //    return this.http.get<any>(
   //       `${environment.typeSense.url}/collections/${
   //          TYPESENSE_COLLECTIONS.FUNDAMENTALS
   //       }/documents/${encodeURIComponent(ticker)}`
   //    );
   // }

   public async getIsUserViewedStock(mainTicker: string): Promise<any> {
      const userId = this.auth.getUserId();
      const filterBy = [
         `user_id:=${userId}`,
         `mainTicker:=${encodeURIComponent(mainTicker)}`,
      ];
      const data = await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.USER_MONTHLY_VIEWED
            }/documents/search?q=*&filter_by=${filterBy.join('&&')}`
         )
      );
      return !!data?.hits?.length;
   }

   public getRecommendation(ticker: string): any {
      return lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.RECOMMENDATION
            }/documents/${encodeURIComponent(ticker)}`
         )
      );
   }

   public getRelatedStocks(params: any): any {
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/search`;
      return lastValueFrom(this.http.get<any>(url, { params: params }));
   }

   public getCompanyNews(ticker: any): any {
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_NEWS
         }/documents/${encodeURIComponent(ticker)}`;
      return lastValueFrom(this.http.get<any>(url));
   }

   public stockNewArray(tickers: any): any {
      const filterBy = [`Symbol:[${tickers.join(',')}]`];
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_NEWS
         }?search?q=*&filter_by=${filterBy.join('&&')}`;
      return lastValueFrom(this.http.get<any>(url));
   }

   public getPressRelease(ticker: any): any {
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.PRESS_RELEASES
         }/documents/${encodeURIComponent(ticker)}`;
      return lastValueFrom(this.http.get<any>(url));
   }

   public etfHoldings(symbol: string) {
      if(!symbol) {
         return of({
            items: [],
            totalCount: 0,
            symbols: [],
         });
      }
      const symbols: any[] = [];
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_HOLDINGS}/documents/${symbol}`;
      return this.http.get<any>(url).pipe(
         map((data) => {
            const item: any = data;
            for (let i in item.holdings) {
               if (item.holdings[i].isin) {
                  symbols.push(item.holdings[i].isin);
               }
            }
            return {
               items: item.holdings,
               totalCount: item.holdings.length,
               symbols: symbols,
            };
         })
      );
   }

   public etfGetInfo(id: string): any {
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_PROFILE}/documents/${id}`;
      return this.http.get<any>(url);
   }

   public async etfComplianceInfo(id: string): Promise<any> {
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_COMPLIANCE_DETAILED}/documents/${id}`;
      return await lastValueFrom(this.http.get<any>(url));
   }

   public etfSectorExposure(id: string): Observable<any> {
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_SECTOR_EXPOSURE}/documents/${id}`;
      return this.http.get<any>(url).pipe(
         map((data) => {
            const item: any = data;
            return item.sectorExposure;
         })
      );
   }

   public etfCountryExposure(id: string): any {
      var url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_COUNTRY_EXPOSURE}/documents/${id}`;
      return this.http.get<any>(url).pipe(
         map((data) => {
            const item: any = data;
            return item.countryExposure;
         })
      );
   }

   public async getInvestorChecklist(ticker: string) {
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.INVESTMENT_CHECKLIST
            }/documents/${encodeURIComponent(ticker)}`
         )
      );
   }

   setData(data: any) {
      this.dividendYieldData.next(data);
   }

   public getForecastPriceTarget(ticker: string): any {
      return this.http.get<any>(
         `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.PRICE_TARGET
         }/documents/${encodeURIComponent(ticker)}`
      );
   }

   public getForecastRevenueEstimates(ticker: string): any {
      return this.http.get<any>(
         `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.REVENUE_ESTIMATES
         }/documents/${encodeURIComponent(ticker)}`
      );
   }

   public getForecastEarningsEstimates(ticker: string): any {
      return this.http.get<any>(
         `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.EARNINGS_ESTIMATES
         }/documents/${encodeURIComponent(ticker)}`
      );
   }

   public async forecastCollection(ticker: string) {
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.FORECASTS
            }/documents/${encodeURIComponent(ticker)}`
         )
      );
   }

   public getBseTickers(ticker: string, code: string) {
      let filterBy: any = [];
      if (ticker) {
         filterBy = [`ticker:=${ticker}`];
      }

      if (code) {
         filterBy = [`code:=${code}`];
      }

      let queryParams = new HttpParams()
         .set('q', '*')
         .set('filter_by', filterBy);

      return lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.BSE_TICKERS}/documents/search`,
            { params: queryParams }
         )
      );
   }

   // public async getCountries() {
   // let queryParams = new HttpParams().set('per_page', '250').set('q', '*');

   // return await lastValueFrom(
   //    this.http.get<any>(
   //       `${environment.typeSense.url}/collections/market_collection/documents/search`,
   //       { params: queryParams }
   //    )
   // );
   // }

   public async getSectorIndustries() {
      let queryParams = new HttpParams().set('per_page', '250').set('q', '*');

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.MUSAFFA_SECTOR}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getEtfClass() {
      let queryParams = new HttpParams().set('per_page', '250').set('q', '*');

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.MUSAFFA_ETF_CLASS}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getAcademyArticles(
      searchText: string,
      filterBy: string,
      perPage: number,
      page: number,
      sortBy: string,
      queryBy?: string
   ): Promise<any> {
      let queryParams = new HttpParams()
         .set('q', searchText)
         .set('filter_by', filterBy)
         .set('per_page', perPage)
         .set('page', page)
         .set('sort_by', sortBy);

      if (queryBy) {
         queryParams = queryParams.set('query_by', queryBy);
      }

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ACADEMY_ARTICLES}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getYearHigh(country: string, perPage: string) {
      const filterBy = [];

      if (country) {
         filterBy.push(`country:=${country?.toUpperCase()}`);
      }
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', 'sortOrder:asc')
         .set('filter_by', filterBy.join('&&'))
         .set('per_page', perPage);
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCK_BUCKET_52WEEKHIGH}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getYearLow(country: string, perPage: string) {
      const filterBy = [];

      if (country) {
         filterBy.push(`country:=${country?.toUpperCase()}`);
      }
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', 'sortOrder:asc')
         .set('filter_by', filterBy.join('&&'))
         .set('per_page', perPage);
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCK_BUCKET_52WEEKLOW}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getMostPopularStocks(country: string, perPage: string) {
      const filterBy = [];
      if (country) {
         filterBy.push('country:=' + country?.toUpperCase());
      }
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', 'search_count:desc')
         .set('filter_by', filterBy.join('&&'))
         .set('per_page', perPage);
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCK_BUCKET_MOSTPOPULAR}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getMostActiveStocks(country: string, perPage: string) {
      const filterBy = [];
      if (country) {
         filterBy.push('country:=' + country?.toUpperCase());
      }
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', 'sortOrder:asc')
         .set('filter_by', filterBy.join('&&'))
         .set('per_page', perPage);
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCK_BUCKET_MOSTACTIVE}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getTopGainer(country: string, perPage: string) {
      const filterBy = [];
      if (country) {
         filterBy.push('country:=' + country?.toUpperCase());
      }
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', 'percentChange:desc')
         .set('filter_by', filterBy.join('&&'))
         .set('per_page', perPage);
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCK_BUCKET_TOPGAINERS}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getTopLosers(country: string, perPage: string) {
      const filterBy = [];
      if (country) {
         filterBy.push('country:=' + country?.toUpperCase());
      }
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', 'percentChange:asc')
         .set('filter_by', filterBy.join('&&'))
         .set('per_page', perPage);
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCK_BUCKET_TOPLOSERS}/documents/search`,
            { params: queryParams }
         )
      );
   }



   public async getEtfDataFromSymbols(symbols: string[]) {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('per_page', 250)
         .set('filter_by', `id:=[${symbols.join(',')}]`);
      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_PROFILE}/documents/search`,
               { params: queryParams }
            )
            .pipe(
               map((response) => {
                  const data = response.hits;
                  const items: any = {};
                  data.forEach((x: any) => {
                     const doc = x.document;
                     items[doc.id] = {
                        assetClass: doc.assetClass,
                        aum: doc.aum,
                        avoid: doc.avoid,
                        cusip: doc.cusip,
                        description: doc.description,
                        domicile: doc.domicile,
                        etfCompany: doc.etfCompany,
                        exchange: doc.exchange,
                        expenseRatio: doc.expenseRatio,
                        halal: doc.halal,
                        identifier: doc.identifier,
                        inceptionDate: doc.inceptionDate,
                        investmentSegment: doc.investmentSegment,
                        isin: doc.isin,
                        mainSymbol: doc.mainSymbol,
                        name: doc.name,
                        nav: doc.nav,
                        navCurrency: doc.navCurrency,
                        notHalal: doc.notHalal,
                        numberOfHoldings: doc.numberOfHoldings,
                        shariahStates: doc.shariahStates,
                        symbol: doc.symbol,
                        trackingIndex: doc.trackingIndex,
                        unCovered: doc.unCovered,
                        ranking: doc.ranking,
                     };
                  });
                  return items;
               })
            )
      );
   }

   public async getStocksDataFromSymbols(symbols: string[]) {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('per_page', 250)
         .set('filter_by', `ticker:=[${symbols.join(',')}]`);
      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}/documents/search`,
               { params: queryParams }
            )
            .pipe(
               map((response) => {
                  const data = response.hits;
                  const items: any = {};
                  data.forEach((x: any) => {
                     const doc = x.document;
                     items[doc.ticker] = {
                        id: doc.id,
                        companyName: doc.name,
                        currency: doc.currency,
                        exchange: doc.exchange,
                        exchangeMarketCap: doc.exchangeMarketCap,
                        identifier: doc.identifier,
                        industry: doc.musaffaIndustryName,
                        isin: doc.isin,
                        mainTicker: doc.mainTicker,
                        logo: doc.logo,
                        marketCapitalization: doc.marketCapitalization,
                        stockName: doc.ticker,
                        sector: doc.musaffaSectorName,
                        percentChange: doc.percentChange,
                        currentPrice: doc.currentPrice,
                        recommededStatus: doc.analyst_recommendation_weighted_avg,
                        complianceStatus: doc.shariahCompliantStatus,
                        complianceRanking: doc.compliantRanking,
                        // totalReturn_1w:doc.totalReturn_1W,
                        // totalReturn_1y: doc.totalReturn_1Y,
                        // totalReturn_5y: doc.totalReturn_5Y,

                     };
                  });
                  return items;
               })
            )
      );
   }

   public async getFilteredStockSymbols(
      page: number,
      perPage: number,
      basicFilters: any,
      advancedFilters: any,
      sortColumn: string,
      sortOrder: string
   ): Promise<any> {
      // console.log('basicFilters', basicFilters);
      // console.log('advancedFilters', advancedFilters);

      const fixedFilters = [
         {
            field: 'status',
            value: 'PUBLISH',
         },
      ];
      const filterBy = this.buildFilterConditions(
         [...fixedFilters, ...basicFilters],
         advancedFilters
      );

      // const sortBy = ['marketcap:desc'];
      const sortBy = [];

      // const sortBy = `${sortColumn}:${sortOrder}`;

      if (sortColumn && sortOrder) {
         sortBy.unshift(`${sortColumn}:${sortOrder}`);
      }

      const queryParams = new HttpParams()
         .set('page', page)
         .set('per_page', perPage ?? 15)
         .set('q', '*')
         .set('filter_by', filterBy.join('&&'))
         .set('sort_by', sortBy.join(','));

      // console.log('asdflhljksdhf');
      // console.log(queryParams.toString());

      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/search`,
               { params: queryParams }
            )
            .pipe(
               map((response) => {
                  const data = response.hits;
                  const items: any = {};
                  data.forEach((x: any) => {
                     const doc = x.document;
                     items[doc.id] = this.mapPricesData(doc);
                     this.stocksDataCache.set(doc.id, {
                        data: items[doc.id],
                        expiresAt: Date.now() + 2 * 60 * 1000 // 2 minutes in milliseconds
                     });
                  });
                  const number_of_pages = Math.ceil(
                     response.found / response.request_params.per_page
                  );
                  return {
                     currentPage: response.page,
                     hasNext: response.page < number_of_pages,
                     hasPrevious: response.page > 1,
                     itemCount: response.request_params.per_page,
                     pageQuantity: number_of_pages,
                     totalCount: response.found,
                     items: items,
                  };
               })
            )
      );
   }

   private buildFilterConditions(basicFilters: any, advancedFilters: any): any {
      const filterBy: string[] = [];

      const processFilters = (filters: any) => {
         return filters
            .filter((filter: any) => filter?.value?.length > 0)
            .map((filter: any) => {
               const field = filter.field;
               const value = filter.value;

               // In-array filter
               if (Array.isArray(value)) {
                  const values = value.map((v: string | number) => (typeof v === 'number') ? v : `\`${v}\``).join(',');
                  return `${field}:=[${values}]`;
               }

               // Range filter
               const [min, max] = value.split(':');
               if (min && max) {
                  // if (field === 'ipoDate') {
                  //    return `${field}:[<${min}]`;
                  // }
                  if (min === '*' && max !== '*') {
                     return `${field}:<=${max}`;
                  }
                  if (min !== '*' && max === '*') {
                     return `${field}:>=${min}`;
                  }
                  if (min !== '*' && max !== '*') {
                     return `${field}:>=${min}&&${field}:<=${max}`;
                  }
               }

               // Not filter
               if (value.includes('!')) {
                  return `${field}:!=${value.replace('!', '')}`;
               }

               return `${field}:=${value}`;
            });
      };

      if (basicFilters.length > 0) {
         const exchangeIndex = basicFilters.findIndex(
            (filter: any) => filter.field === 'exchange'
         );
         const countryIndex = basicFilters.findIndex(
            (filter: any) => filter.field === 'country' || filter.field === 'domicile'
         );
         const complianceStatusIndex = basicFilters.findIndex(
            (filter: any) => filter.field === 'musaffaHalalRating'
         );
         if (
            exchangeIndex === -1 ||
            (exchangeIndex > -1 && !basicFilters[exchangeIndex]?.value?.length)
         ) {
            this.getStocksExchangeData(basicFilters[countryIndex].value?.toUpperCase(), 'stock').then((data: any) => {
               basicFilters.push({
                  field: 'exchange',
                  value: data,
               });
            });
         }
         if (complianceStatusIndex !== -1) {
            if (!basicFilters[complianceStatusIndex]?.value?.length) {
               basicFilters[complianceStatusIndex].value.push(
                  'COMPLIANT',
                  'NON_COMPLIANT',
                  'QUESTIONABLE'
               );
            }
         } else {
            basicFilters.push({
               field: 'musaffaHalalRating',
               value: ['COMPLIANT', 'NON_COMPLIANT', 'QUESTIONABLE'],
            });
         }
         filterBy.push(...processFilters(basicFilters));
      }

      if (advancedFilters.length > 0) {
         filterBy.push(...processFilters(advancedFilters));
      }

      return filterBy;
   }

   mapPricesData(doc: any) {
      if (!doc.lastPrice) {
         doc.lastPrice = doc.currentPrice;
      }
      doc.price = doc.currentPrice ?? doc.currentPrice ?? doc.CurrentPrice;
      doc.priceChange =
         doc.priceChange1D !== undefined
            ? isNaN(doc.priceChange1D)
               ? parseFloat(doc.priceChange1D)
               : doc.priceChange1D
            : 0;
      doc.priceChangePercent =
         doc.priceChange1DPercent !== undefined
            ? isNaN(doc.priceChange1DPercent)
               ? parseFloat(doc.priceChange1DPercent)
               : doc.priceChange1DPercent
            : 0;
      doc.percentChange =
         doc.priceChange1DPercent !== undefined
            ? isNaN(doc.priceChange1DPercent)
               ? parseFloat(doc.priceChange1DPercent)
               : doc.priceChange1DPercent
            : 0;
      doc.change = doc.priceChange;

      doc.close = doc.close;
      doc.datetime = doc.priceLastUpdated;
      doc.longPeriodChange = doc.longPeriodChange;
      doc.longPeriodPercentChange = doc.longPeriodPercentChange;
      doc.fullDatetime = doc.priceLastUpdated;
      doc.dividendYield = doc.currentDividendYieldTTM;
      doc.ticker = doc.ticker;
      doc.todaysLow = doc.low;
      doc.todaysHigh = doc.high;
      doc.todaysPercent =
         ((doc.currentPrice - doc.low) / (doc.high - doc.low)) * 100;
      doc.todaysOpen = doc.open;
      doc.volume = doc.volume;
      // doc.peBasicExclExtraTtm = doc.peBasicExclExtraTTM;
      doc.peAnnual = doc.peAnnual;
      doc.fiftyTwoWeekLow = doc['52WeekLow'];
      doc.fiftyTwoWeekHigh = doc['52WeekHigh'];
      doc.fiftyTwoWeekPercent =
         ((doc.currentPrice - doc['52WeekLow']) /
            (doc['52WeekHigh'] - doc['52WeekLow'])) *
         100;
      doc.averageVolume = doc.avgVolume30days;
      // doc.status = doc.status;
      return doc;
   }

   // private mapFundamentalsData(doc: any) {
   //    // console.log('doc', doc);
   //    return {
   //       todaysPercent: doc.priceChange1DPercent,
   //       todaysLow: doc.low,
   //       todaysHigh: doc.high,
   //       todaysOpen: doc.open,
   //       volume: doc.volume,
   //       peBasicExclExtraTtm: doc.peBasicExclExtraTtm,
   //       // fiftyTwoWeekPercent: doc.fiftyTwoWeekPercent,
   //       fiftyTwoWeekLow: doc['52WeekLow'],
   //       fiftyTwoWeekHigh: doc['52TwoWeekHigh'],
   //       averageVolume: doc.avgVolume30days
   //    };
   // }

   public async getEtfsData(
      country: string,
      page: number,
      per_page: number,
      sort_by: string,
      filter_by: any
   ): Promise<any> {
      const items: any = {};
      let params = new HttpParams()
         .set('q', '*')
         .set('country', country)
         .set('page', page)
         .set('per_page', per_page)
         .set('sort_by', sort_by)
         .set('filter_by', filter_by);

      return await lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETFS_DATA}/documents/search`,
               { params: params }
            )
            .pipe(
               map(async (item) => {
                  const symbols: string[] = [];
                  const data: any = item.hits;
                  data.forEach(async (x: any) => {
                     const doc = x.document;
                     items[doc.id] = this.mapPricesData(doc)
                     symbols.push(doc.id);
                  });
                  const etfProfiles = await this.getEtfFromSymbols(symbols);
                  for (let i in etfProfiles) {
                     items[i] = Object.assign({}, etfProfiles[i], items[i]);
                  }
                  return items;
               })
            )
      );
   }

   public async getEtfPrices(symbols: string[]): Promise<any> {
      symbols = (symbols || []).filter((symbol: any) => typeof symbol === 'string' && !!symbol.length);
      const chunkSize = 40;
      const items: any = {};
      for (let i = 0; i < symbols.length; i += chunkSize) {
         const chunk = symbols.slice(i, i + chunkSize);
         let newArr: any = [];
         chunk.forEach((item) => {
            let symbol = '`' + item + '`';
            newArr.push(symbol);
         });
         let filterStr = newArr.join(',');

         let queryParams = new HttpParams()
            .set('per_page', 250)
            .set('q', '*')
            .set('filter_by', `id:=[${filterStr}]`);

         await lastValueFrom(
            this.http
               .get<any>(
                  `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETFS_DATA}/documents/search`,
                  { params: queryParams }
               )
               .pipe(
                  map((item) => {
                     const data: any = item.hits;
                     data.forEach((x: any) => {
                        const doc = x.document;
                        items[doc.id] = this.mapPricesData(doc);
                     });
                     return items;
                  })
               )
         );
      }
      return items;
   }

   public clearStocksDataCache() {
      this.stocksDataCache.clear();
   }

   public async getEtfProfileAndDataFromSymbols(symbols: string[]): Promise<any> {
      symbols = (symbols || []).filter((symbol: any) => typeof symbol === 'string' && !!symbol.length);
      const chunkSize = 40;
      const items: any = {};
      const symbolsToFetch = symbols.filter((symbol) => {
         const cached = this.stocksDataCache.get(symbol);
         if (cached?.data?.companyName && (cached && cached.expiresAt > Date.now())) {
            items[symbol] = cached.data;
            return false;
         } else {
            return true;
         }
      });
      for (let i = 0; i < symbolsToFetch.length; i += chunkSize) {
         const chunk = symbolsToFetch.slice(i, i + chunkSize);
         let newArr: any = [];
         chunk.forEach((item) => {
            let symbol = '`' + item + '`';
            newArr.push(symbol);
         });
         let filterStr = newArr.join(',');

         const etfProfileFields = [
            'name',
            'navCurrency',
            'symbol',
            'description'
         ];

         let queryParams = new HttpParams()
            .set('per_page', 200)
            .set('q', '*')
            .set('include_fields', `$${TYPESENSE_COLLECTIONS.ETF_PROFILE}(${etfProfileFields.join(',')})`)
            .set('filter_by', `$${TYPESENSE_COLLECTIONS.ETF_PROFILE}(id:*)&&id:=[${filterStr}]`);


         await lastValueFrom(
            this.http
               .get<any>(
                  `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETFS_DATA}/documents/search`,
                  { params: queryParams }
               )
               .pipe(
                  map((item) => {
                     const data: any = item.hits;
                     data.forEach((x: any) => {
                        // const doc = x.document;
                        const { [TYPESENSE_COLLECTIONS.ETF_PROFILE]: etfProfile, ...doc } = x.document;
                        items[doc.id] = {
                           ...etfProfile,
                           ...doc,
                        };
                     });
                     return items;
                  })
               )
         );
      }
      return items;
   }

   public async getCompanyProfileAndDataFromSymbols(symbols: string[], getHeadquarters: boolean = false, onlyProfileAndCompliance: boolean = false): Promise<any> {
      symbols = (symbols || []).filter((symbol: any) => typeof symbol === 'string' && !!symbol.length);
      const chunkSize = 40;
      const items: any = {};
      const symbolsToFetch = symbols.filter((symbol) => {
         const cached = this.stocksDataCache.get(symbol);
         if (cached?.data?.companyName && (cached && cached.expiresAt > Date.now())) {
            items[symbol] = cached.data;
            return false;
         } else {
            return true;
         }
      });
      for (let i = 0; i < symbolsToFetch.length; i += chunkSize) {
         const chunk = symbolsToFetch.slice(i, i + chunkSize);
         let newArr: any = [];
         chunk.forEach((item) => {
            let symbol = '`' + item + '`';
            newArr.push(symbol);
         });
         let filterStr = newArr.join(',');

         const companyProfileFields = [
            'name',
            'logo',
            'cp_country',
            'city'
         ];

         let stocksDataFields: string[] = [];
         if (onlyProfileAndCompliance) {
            stocksDataFields = [
               'id',
               'sharia_compliance',
               'ranking',
               'ranking_v2',
            ];
         }

         let queryParams = new HttpParams()
            .set('per_page', 200)
            .set('q', '*')
            .set('include_fields', `$${TYPESENSE_COLLECTIONS.STOCKS_DATA}(${companyProfileFields.join(',')})${stocksDataFields ? ','+stocksDataFields.join(',') : ''}`)
            .set('filter_by', `$${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}(id:*)&&id:=[${filterStr}]`);


         await lastValueFrom(
            this.http
               .get<any>(
                  `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/search`,
                  { params: queryParams }
               )

               .pipe(
                  map((item) => {
                     const data: any = item.hits;
                     data.forEach((x: any) => {
                        // const doc = x.document;
                        const { [TYPESENSE_COLLECTIONS.COMPANY_PROFILE]: companyProfile, ...doc } = x.document;
                        items[doc.id] = {
                           ...companyProfile,
                           ...this.mapPricesData(doc),
                           ticker: doc.id,
                           ...(getHeadquarters ? { headquarters: `${companyProfile.city ? companyProfile.city + ',' : ''} ${getCountryNameByCode(companyProfile.cp_country)}` } : {})
                        };
                     });
                     return items;

                  })

               )
         );
      }
      return items;
   }

   public async getPrices(symbols: string[]): Promise<any> {
      symbols = (symbols || []).filter((symbol: any) => typeof symbol === 'string' && !!symbol.length);
      const chunkSize = 40;
      const items: any = {};
      const symbolsToFetch = symbols.filter((symbol) => {
         const cached = this.stocksDataCache.get(symbol);
         if (cached && cached.expiresAt > Date.now()) {
            items[symbol] = cached.data;
            return false;
         } else {
            return true;
         }
      });
      for (let i = 0; i < symbolsToFetch.length; i += chunkSize) {
         const chunk = symbolsToFetch.slice(i, i + chunkSize);
         let newArr: any = [];
         chunk.forEach((item) => {
            let symbol = '`' + item + '`';
            newArr.push(symbol);
         });
         let filterStr = newArr.join(',');

         let queryParams = new HttpParams()
            .set('per_page', 250)
            .set('q', '*')
            .set('filter_by', `id:=[${filterStr}]`);

         await lastValueFrom(
            this.http
               .get<any>(
                  `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/search`,
                  { params: queryParams }
               )
               .pipe(
                  map((item) => {
                     const data: any = item.hits;
                     data.forEach((x: any) => {
                        const doc = x.document;
                        items[doc.id] = this.mapPricesData(doc);
                     });
                     return items;
                  })
               )
         );
      }
      return items;
   }

   public getEarningSurprises(ticker: string) {
      return lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.EARNINGS_SURPRISES
            }/documents/${encodeURIComponent(ticker)}`
         )
      );
   }

   public getEarningEstimates(ticker: string) {
      return lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.EARNINGS_ESTIMATES
               }/documents/${encodeURIComponent(ticker)}`
            )
            .pipe(
               map((item) => {
                  const items: any = {
                     data: [],
                  };
                  const data: any = item.data ?? item.Data;
                  data.forEach((x: any) => {
                     items.data.push({
                        epsAvg: x.epsAvg ?? x.EpsAvg,
                        epsHigh: x.epsHigh ?? x.EpsHigh,
                        epsLow: x.epsLow ?? x.EpsLow,
                        numberAnalysts: x.numberAnalysts ?? x.NumberAnalysts,
                        period: x.period ?? x.Period,
                        year: x.year ?? x.Year,
                        quarter: x.quarter ?? x.Quarter,
                     });
                  });
                  return items;
               })
            )
      );
   }

   public getEarningCalendars(ticker: string) {
      return lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.EARNINGS_CALENDAR
               }/documents/${encodeURIComponent(ticker)}`
            )
            .pipe(
               map((item) => {
                  const items: any = {
                     earningsCalendar: [],
                  };
                  const data: any =
                     item.earningsCalendar ?? item.EarningsCalendar;
                  data.forEach((x: any) => {
                     items.earningsCalendar.push({
                        date: x.date ?? x.Date,
                        epsActual: x.epsActual ?? x.EpsActual,
                        epsEstimate: x.epsEstimate ?? x.EpsEstimate,
                        hour: x.hour ?? x.Hour,
                        quarter: x.quarter ?? x.Quarter,
                        revenueActual: x.revenueActual ?? x.RevenueActual,
                        revenueEstimate: x.revenueEstimate ?? x.RevenueEstimate,
                        symbol: x.symbol ?? x.Symbol,
                        year: x.year ?? x.Year,
                     });
                  });
                  return items;
               })
            )
      );
   }

   public getRevenueEstimates(ticker: string) {
      return lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.REVENUE_ESTIMATES
               }/documents/${encodeURIComponent(ticker)}`
            )
            .pipe(
               map((item) => {
                  const items: any = {
                     data: [],
                  };
                  const data: any = item.data ?? item.Data;
                  data.forEach((x: any) => {
                     items.data.push({
                        lastUpdate: x.lastUpdate ?? x.LastUpdate,
                        numberAnalysts: x.numberAnalysts ?? x.NumberAnalysts,
                        period: x.period ?? x.Period,
                        revenueAvg: x.revenueAvg ?? x.RevenueAvg,
                        revenueHigh: x.revenueHigh ?? x.RevenueHigh,
                        revenueLow: x.revenueLow ?? x.RevenueLow,
                        year: x.year ?? x.Year,
                        quarter: x.quarter ?? x.Quarter,
                     });
                  });
                  return items;
               })
            )
      );
   }

   public getPriceTarget(ticker: string) {
      return lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.PRICE_TARGET
               }/documents/${encodeURIComponent(ticker)}`
            )
            .pipe(
               map((item) => {
                  return {
                     lastUpdated: item.lastUpdated ?? item.LastUpdated,
                     symbol: item.symbol ?? item.Symbol,
                     targetHigh: item.targetHigh ?? item.TargetHigh,
                     targetLow: item.targetLow ?? item.TargetLow,
                     targetMean: item.targetMean ?? item.TargetMean,
                     targetMedian: item.targetMedian ?? item.TargetMedian,
                     id: item.id ?? item.Id,
                  };
               })
            )
      );
   }

   public getInvestmentChecklistVariables() {
      return lastValueFrom(
         this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.INVESTMENT_CHECKLIST_VARIABLES}/documents/search?q=*&per_page=250`
            )
            .pipe(
               map((item) => {
                  const data: any = item.hits;
                  const items: Array<InvestmentChecklistVariableModel> = [];
                  data.forEach((x: any) => {
                     const doc: InvestmentChecklistVariableModel = x.document;
                     items.push(doc);
                  });
                  return items;
               })
            )
      );
   }

   async getMatketNews<T>(): Promise<T> {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', 'Datetime:desc')
         .set('per_page', '10')
         .set('page', '1');

      return await lastValueFrom(
         this.http.get<T>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.MARKET_NEWS}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getSuperInvestors(
      sort_by: string,
      perPage: number,
      page: number
   ) {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('sort_by', sort_by)
         .set('per_page', perPage)
         .set('page', page);

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.SUPER_INVESTORS}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getSuperInvestorsPortfolio(
      cikId: string,
      perPage: number,
      sortBy: string,
      page: number
   ) {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('filter_by', `cik_id:${cikId}`)
         .set('per_page', perPage)
         .set('sort_by', sortBy)
         .set('page', page);

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.SUPER_INVESTORS_PORTFOLIO}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getHalalStockBuckets(
      isVisible: string,
      perPage: number,
      sortOrder: string
   ) {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('filter_by', isVisible)
         .set('per_page', perPage)
         .set('sort_by', sortOrder);

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.HALAL_STOCK_BUCKETS}/documents/search`,
            { params: queryParams }
         )
      );
   }

   searchOnlyStockSymbols(name: string, stockCountries: any[] = []) {
      if (name === '') {
         return of([]);
      }

      const searches = [
         {
            collection: TYPESENSE_COLLECTIONS.COMPANY_PROFILE,
            q: name,
            query_by: 'name,ticker',
            prioritize_token_position: true,
            sort_by: '_text_match:desc,tickerIsMain:desc,usdMarketCap:desc',
            query_by_weights: '1,2',
            filter_by: `status:=PUBLISH&&country:=[${stockCountries}]`,
         },
      ];

      return this.http
         .post<any>(environment.typeSense.url + '/multi_search', { searches })
         .pipe(
            map((item) => {
               const results: any = item?.results;
               const ret: any[] = [];

               results.forEach((item: any) => {
                  const data: any = item.hits;
                  if (data) {
                     this.processStockData(data, ret, name);
                  }
               });

               return ret.sort((a, b) => b.textMatch - a.textMatch);
            })
         );
   }

   async getInvestmentProgress() {
      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.INVESTMENT_PROGRESSBAR}/documents/1`
         )
      );
   }

   async searchSuperInvestorStocks(
      searchQuery: string,
      cikId: string,
      perPage: number,
      sortBy: string,
      page: number
   ) {
      const query =
         searchQuery && searchQuery.trim() !== '' ? searchQuery : '*';

      let queryParams = new HttpParams()
         .set('q', query)
         .set('query_by', 'name,symbol')
         .set('filter_by', `cik_id:${cikId}`)
         .set('per_page', perPage)
         .set('sort_by', sortBy)
         .set('page', page);

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.SUPER_INVESTORS_PORTFOLIO}/documents/search`,
            { params: queryParams }
         )
      );
   }

   async getSuperInvestorsByStocks(
      searchQuery: string,
      filter_by: string,
      perPage: number,
      page: number,
      sortBy: string
   ) {
      let queryParams = new HttpParams()
         .set('q', searchQuery)
         .set('filter_by', `symbol:=[${filter_by}]`)
         .set('per_page', perPage)
         .set('page', page)
         .set('sort_by', sortBy);

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.SUPER_INVESTORS_PORTFOLIO}/documents/search`,
            { params: queryParams }
         )
      );
   }

   public async getSuperInvestorsDataFromIds(
      filter_by: any,
      per_page: number = 250
   ) {
      let queryParams = new HttpParams()
         .set('q', '*')
         .set('filter_by', `cik:=[${filter_by}]`)
         .set('per_page', per_page);

      return await lastValueFrom(
         this.http.get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.SUPER_INVESTORS}/documents/search`,
            { params: queryParams }
         )
      );
   }

   async getStocksComplianceInfo(symbols: string[]): Promise<any> {
      try {
         let complianceInfo = await this.getStockFromSymbols(symbols).then(
            (data: any) => {
               return Object.entries<{ [key: string]: any }>(data).map(
                  ([key, value]) => ({
                     symbol: key,
                     isin: value.isin,
                     mainTicker: value.mainTicker,
                     name: value.name,
                     logo: value.logo ? value.logo : '',
                     country: value.country,
                     complianceRanking: value.compliantRanking ?? '',
                     complianceRankingV2: value.ranking_v2 ?? '',
                     complianceStatus: value.shariahCompliantStatus,
                     currency: value.currency,
                     usdMarketCap: value.usdMarketCap,
                     type: 'stock',
                  })
               );
            }
         );
         return complianceInfo;
      } catch (error) {
         console.error(error);
         throw error;
      }
   }

   async getEtfsComplianceInfo(symbols: any): Promise<any> {
      try {
         let complianceInfo = await this.getEtfFromSymbols(symbols).then(
            (data: any) => {
               return Object.entries<{ [key: string]: any }>(data).map(
                  ([key, value]) => ({
                     symbol: key,
                     isin: value.isin,
                     aum: value.aum,
                     name: value.name,
                     logo: value.logo ? value.logo : '',
                     // country: value.country,
                     complianceRanking: value.ranking ?? '',
                     complianceRankingV2: value.ranking_v2 ?? '',
                     complianceStatus: value.shariahStates,
                     currency: value.navCurrency,
                     type: 'etf',
                  })
               );
            }
         );
         return complianceInfo;
      } catch (error) {
         console.error(error);
         throw error;
      }
   }

   getWebsiteCollectionData(): Observable<WebsiteStats> {
      this.websiteStats$ =
         this.websiteStats$ ??
         this.fetchWebsiteCollection().pipe(shareReplay());
      return this.websiteStats$;
   }

   private fetchWebsiteCollection(): Observable<WebsiteStats> {
      return this.http
         .get<WebsiteStats>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.WEBSITE}/documents/stats`,
            {}
         )
         .pipe(map((response) => response));
   }

   getPortfolioStats(): Observable<any> {
      this.portfolioStats$ =
         this.portfolioStats$ ?? this.fetchPortfolioStats().pipe(shareReplay());
      return this.portfolioStats$;
   }

   private fetchPortfolioStats(): Observable<any> {
      return this.http
         .get<any>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.WEBSITE}/documents/portfolio`,
            {}
         )
         .pipe(map((response) => response));
   }

   // private crowdfundingProgress$?: Observable<CrowdfundingProgressbarResponse>;
   fetchCrowdfundingProgressData(): Observable<CrowdfundingProgressbarResponse> {
      // if (!this.crowdfundingProgress$) {
      return this.http
         .get<CrowdfundingProgressbarResponse>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.INVESTMENT_PROGRESSBAR}/documents/1`,
            {}
            )
            .pipe(
               shareReplay(1),
               map((response) => response)
            );
      // }
      // return this.crowdfundingProgress$;
   }

   // private crowdfundingProgress$?: Observable<CrowdfundingProgressbarResponse>;
   fetchCrowdfundingProgressData3(): Observable<CrowdfundingProgressbarResponse3> {
      // if (!this.crowdfundingProgress$) {
      return this.http
         .get<CrowdfundingProgressbarResponse3>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.INVESTMENT_PROGRESSBAR}/documents/3`,
            {}
            )
            .pipe(
               shareReplay(1),
               map((response) => response)
            );
      // }
      // return this.crowdfundingProgress$;
   }

   async getStocksDataBySymbols(symbols: string[]): Promise<any> {

      const queryParams = new HttpParams()
         .set('per_page', 250)
         .set('q', '*')
         .set('filter_by', `ticker:=[${symbols.join(',')}]&&sharia_compliance:=['COMPLIANT','NON_COMPLIANT','QUESTIONABLE']&&isMainTicker:=1`)

      return await lastValueFrom(
         this.http
            .get<any>(`${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/search`, { params: queryParams })
            .pipe(
               map((response) => {
                  const data = response.hits;
                  const items: any = {};
                  data.forEach((x: any) => {
                     const doc = x.document;
                     items[doc.id] = this.mapPricesData(doc);
                  });
                  return {
                     items: items,
                  }
               })
            )
      )
   }

   async getStocksDataBySymbolsWatchlist(symbols: string[]): Promise<any> {

      const queryParams = new HttpParams()
         .set('per_page', 250)
         .set('q', '*')
         .set('filter_by', `id:=[${symbols.join(',')}]`)

      return await lastValueFrom(
         this.http
            .get<any>(`${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/search`, { params: queryParams })
            .pipe(
               map((response) => {
                  const data = response.hits;
                  const items: any = {};
                  data.forEach((x: any) => {
                     const doc = x.document;
                     items[doc.id] = this.mapPricesData(doc);
                  });
                  return {
                     items: items,
                  }
               })
            )
      )
   }

   async getFrontendConfig(): Promise<any> {
      // const queryParams = new HttpParams()
      //    .set('per_page', 250)
      //    .set('q', '*')
      return await lastValueFrom(
         this.http.get<any>(`${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.FRONTEND_CONFIG}/documents/default_config`)
      );
   }

   async getETFsDataBySymbols(symbols: string[]): Promise<any> {

      const queryParams = new HttpParams()
         .set('per_page', 250)
         .set('q', '*')
         .set('filter_by', `symbol:=[${symbols.join(',')}]&&shariahCompliantStatus:=['COMPLIANT','NON_COMPLIANT','QUESTIONABLE']`)

      return await lastValueFrom(
         this.http
            .get<any>(`${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETFS_DATA}/documents/search`, { params: queryParams })
            .pipe(
               map((response) => {
                  const data = response.hits;
                  const items: any = {};
                  data.forEach((x: any) => {
                     const doc = x.document;
                     items[doc.id] = this.mapPricesData(doc);
                  });
                  return {
                     items: items,
                  }
               })
            )
      )
   }

   get50PercentOffEndDate(): Observable<PercentOffEndDate> {
      if (!this.endDate$) {
         this.endDate$ = this.http.get<PercentOffEndDate>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.WEBSITE}/documents/homepage_banner`
         )
         .pipe(
            map(response => response),
            shareReplay(1)
         );
      }
      return this.endDate$;
   }

   getBannersVisibilityData(): Observable<BannersVisibility> {
      // If data is already fetched, return cached observable
      if (!this.bannersVisibilityData$) {
         this.bannersVisibilityData$ = this.http.get<BannersVisibility>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.WEBSITE}/documents/bannersVisibility`
         ).pipe(
            map(response => response),
            shareReplay(1), // caches the last emitted value
         );
      }
      return this.bannersVisibilityData$;
   }

   getScreenMethodologies(): Observable<Methodology[]> {
      return this.http
         .get<RawMethodologyResponse>(`${environment.uatTypeSense.url}/collections/${TYPESENSE_COLLECTIONS.ISLAMIC_FINANCIAL_SCREENING}/documents/search?q=*`)
         .pipe(
            map(res => res.hits?.map(h => h.document) ?? [])
         );
   }
   // getScreenMethodologies(): Observable<Methodology[]> {
   //    return this.http
   //       .get<RawMethodologyResponse>( `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ISLAMIC_FINANCIAL_SCREENING}/documents/search?q=*` )
   //       .pipe(
   //          map(res => res.hits?.map(h => h.document) ?? [])
   //       );
   // }
   // getBannersVisibilityData(): Observable<BannersVisibility> {
   //    const mockData: BannersVisibility = {
   //      carousalBanner: true,
   //      id: 'bannersVisibility',
   //      popupBanner: true,
   //      popupBannerRiba: false,
   //      stickyBanner: true
   //    };
    
   //    return of(mockData).pipe(shareReplay(1));
   // }

   getFoundationOfferData(): Observable<FoundationOffer> {
      // If data is already fetched, return cached observable
      if (!this.foundationOfferData$) {
         this.foundationOfferData$ = this.http.get<FoundationOffer>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.WEBSITE}/documents/pricing_page`
         ).pipe(
            map(response => response),
            shareReplay(1), // caches the last emitted value
         );
      }
      return this.foundationOfferData$;
   }

   // getFoundationOfferData(): Observable<FoundationOffer> {
   //    return of({
   //      id: 'pricing_page',
   //      foundation_monthly_offer_chip: '🎉 Intro Price',
   //      foundation_yearly_offer_chip: '🎉 Intro Price',
   //      foundation_monthly_offer_subtext: 'Limited-time offer. Regular $7.50/month',
   //      foundation_yearly_offer_subtext: 'Limited-time offer. Regular $5/month'
   //    });
   // }

   loadCountryCoverageData(): Observable<CountryCoverageRecord[]> {
      const url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.COUNRTY_COVERAGE_COLLECTION}/documents/search?q=*&per_page=100&sort_by=total_stocks:desc`;

      return this.http
         .get<CountryCoverageResponse>(url)
         .pipe(
            map(res => {
               const docs = res.hits
                  .map(h => h.document)
                  .filter(d => d.is_covered === 'true');

               // Simple priority: countries with ETFs first
               const priority = {
                  hasEtfs: 1,    // total_etfs > 0
                  noEtfs: 2      // missing or 0
               };

               return docs.sort((a, b) => {
                  const aKey = (a.total_etfs ?? 0) > 0 ? 'hasEtfs' : 'noEtfs';
                  const bKey = (b.total_etfs ?? 0) > 0 ? 'hasEtfs' : 'noEtfs';

                  return priority[aKey] - priority[bKey];
               });
            }),
            shareReplay(1)
         );
   }

   getAllCountriesCoverageData(): Observable<CountryCoverageRecord[]> {
      if(!this.countryCoverageData$) {
         this.countryCoverageData$ = this.loadCountryCoverageData()
      }
      return this.countryCoverageData$
   }

   getAllCountryCoverageDataById(countryCode: string): Observable<CountryCoverageRecord | null> {
      return this.getAllCountriesCoverageData().pipe(
         map(countries => {
            const country = countries.find(c => c.id === countryCode);
            return country || null;
         })
      )
   }

   fetchTradingCountriesData(searchText: string = ''): Observable<TradingCountryInfo[]> {
      const isSearch = !!searchText.trim();

      const query = isSearch ? encodeURIComponent(searchText) : '*';

      const url = `${environment.typeSense.url}/collections/` + `${TYPESENSE_COLLECTIONS.TRADING_COUNTRIES}/documents/search` + `?q=${query}&query_by=country_name&per_page=250`;

      // ✅ Cache ONLY the full dataset (no search)
      if (!isSearch) {
         if (!this.tradingCountriesData$) {
            this.tradingCountriesData$ = this.http
               .get<TradingCountryResponse>(url)
               .pipe(
                  map(response => this.sortCountries(response)),
                  shareReplay(1)
               );
         }
         return this.tradingCountriesData$;
      }

      // ✅ Searches are NEVER cached
      return this.http.get<TradingCountryResponse>(url).pipe(
         map(response => this.sortCountries(response))
      );
   }

   private sortCountries(response: TradingCountryResponse): TradingCountryInfo[] {
      const docs = response.hits.map(hit => hit.document);

      const priority: Record<string, number> = {
         'Available Now': 1,
         'Coming Soon': 2,
         'Not Covered': 3
      };

      return docs.sort(
         (a, b) => priority[a.status] - priority[b.status]
      );
   }
   
   getConfigData(): Observable<any> {
      // return of({
      //    "id": "config",
      //    "prod": {
      //       "banners": {
      //          "c_banner1": {
      //             "display": true,
      //             "order": 1
      //          },
      //          "c_banner2": {
      //             "display": true,
      //             "order": 2
      //          },
      //          "c_banner3": {
      //             "display": true,
      //             "order": 3
      //          },
      //          "c_banner4": {
      //             "display": true,
      //             "order": 4
      //          }
      //       },
      //       "questionnaire_reward": true,
      //       "show_event_popup": false,
      //       "sticky_banner": {
      //          "countdown_date": "2026-03-11 03:59:59",
      //          "display": false,
      //          "order": 1
      //       }
      //    }
      // });

      if (!this.configData$) {
         this.configData$ = this.http
            .get<any>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.WEBSITE}/documents/config`
            )
            .pipe(
               catchError(error => {
                  console.error('Failed to load config data', error);
                  this.configData$ = undefined; // allow retry
                  return throwError(() => error);
               }),
               shareReplay(1)
            );
      }
      return this.configData$;
   }

   fetchCrowdfundingProgressData5(): Observable<CrowdfundingProgressbarResponse5> {
      if (!this.progressbar5$) {
         this.progressbar5$ = this.http
            .get<CrowdfundingProgressbarResponse5>(
               `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.INVESTMENT_PROGRESSBAR}/documents/5`
            )
            .pipe(
               shareReplay(1)
            );
      }

      return this.progressbar5$;
   }
}
